import React from 'react';
import PropTypes from 'prop-types';

/* FSC Training - shared building blocks for the workflow guides.
 *
 * Every guide under this folder is written against one Department →
 * Category → Sub-Category path of the live FSC case flows
 * (Prosperity_Workflow_Create_New_Case_Ops and _Trading, launched from the
 * Case "New Case" action via Prosperity_Workflow_Create_New_Case). The pages
 * are mostly data; this file owns the look so they stay consistent.
 *
 * Served at /fsc-training/... as PUBLIC standalone pages (see index.js):
 * linked from the employee Resource Library, the B2B portal and FSC's
 * resource section. So no API calls, no session hooks and no client data in
 * these pages - form instructions only. The copy is hard-coded, so a change in
 * the FSC flow is not picked up automatically - re-read the flow and edit the
 * guide.
 *
 * House style: single hyphen "-" only, and quote every FSC label, question
 * and option verbatim, odd capitalisation included. Readers match these
 * strings against the screen.
 */

export const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
};

const text = { color: brand.charcoal, fontSize: '0.9rem' };

export const TEAM_EMAIL = {
  operations: 'operations@prosperitycapitaladvisors.com',
  trading:    'tradingops@prosperitycapitaladvisors.com',
};

export function Section({ title, icon, children }) {
  return (
    <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', overflow: 'hidden', marginBottom: '1.5rem' }}>
      <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <i className={`bi bi-${icon}`} style={{ color: brand.goldenrod }} />
        <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>{title}</h5>
      </div>
      <div style={{ padding: '1.25rem' }}>{children}</div>
    </div>
  );
}

// Page shell: FSC Training header, then a title card with the routing path
// (department → category → sub-category), audience and an intro paragraph.
export function GuidePage({ title, icon, path, minutes, intro, children }) {
  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h4 style={{ color: brand.deepBlue, fontWeight: 800, margin: 0 }}>FSC Training</h4>
        <p style={{ color: brand.charcoal, margin: 0, fontSize: '0.875rem' }}>
          How-to guides for Salesforce Financial Services Cloud
        </p>
      </div>

      <Section title={title} icon={icon}>
        <p style={{ ...text, marginBottom: '0.4rem' }}>
          <strong>{path}</strong> · for advisors and office staff{minutes ? ` · about ${minutes} minutes` : ''}
        </p>
        <p style={{ ...text, margin: 0, maxWidth: '66ch' }}>{intro}</p>
      </Section>

      {children}
    </div>
  );
}

export function Para({ children, last }) {
  return <p style={{ ...text, marginBottom: last ? 0 : '0.75rem', maxWidth: '68ch' }}>{children}</p>;
}

export function Bullets({ items }) {
  return (
    <ul style={{ ...text, paddingLeft: '1.25rem', marginBottom: '0.75rem' }}>
      {items.map((item, i) => (
        <li key={i} style={{ marginBottom: '0.4rem' }}>{item}</li>
      ))}
    </ul>
  );
}

// A highlighted aside. tone="warn" for things that block or NIGO a request.
export function Callout({ children, tone }) {
  const warn = tone === 'warn';
  return (
    <div style={{ background: warn ? '#fdf6e6' : '#eef5fa', borderLeft: `3px solid ${warn ? brand.goldenrod : brand.tealBlue}`, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.875rem', color: brand.charcoal }}>
      {warn && <i className="bi bi-exclamation-triangle-fill me-2" style={{ color: brand.goldenrod }} />}
      {children}
    </div>
  );
}

// Two-column label → description grid. rows: [[label, description], ...]
export function FieldGrid({ rows }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '0.4rem 1.25rem', fontSize: '0.875rem', marginBottom: '0.75rem' }}>
      {rows.map(([label, desc]) => (
        <React.Fragment key={label}>
          <div style={{ fontWeight: 700, color: brand.deepBlue, whiteSpace: 'nowrap' }}>{label}</div>
          <div style={{ color: brand.charcoal }}>{desc}</div>
        </React.Fragment>
      ))}
    </div>
  );
}

// Numbered steps. steps: [{ title, body }]
export function Steps({ steps }) {
  return (
    <div style={{ display: 'grid', gap: '1.25rem' }}>
      {steps.map((step, i) => (
        <div key={step.title} style={{ display: 'flex', gap: '1rem' }}>
          <div style={{ flex: '0 0 auto', width: '28px', height: '28px', borderRadius: '50%', background: brand.deepBlue, color: 'white', fontWeight: 800, fontSize: '0.8rem', lineHeight: '28px', textAlign: 'center' }}>
            {i + 1}
          </div>
          <div style={{ flex: '1 1 auto', minWidth: 0, color: brand.charcoal, fontSize: '0.9rem' }}>
            <div style={{ fontWeight: 700, color: brand.deepBlue, marginBottom: '0.35rem' }}>{step.title}</div>
            {step.body}
          </div>
        </div>
      ))}
    </div>
  );
}

// The case Subject is a firm policy, so every guide shows it the same way.
// `example` is a full example subject ("Roth conversion - 0989-022356").
// Cases with no client account (staff, Salesforce requests) pass
// noAccount: true and get a plain description instead.
export function subjectRow(example, { noAccount } = {}) {
  return ['Subject', noAccount
    ? <>Required, under 80 characters. There is no client account on this request, so a short plain description - <strong>&ldquo;{example}&rdquo;</strong>.</>
    : <>Required, under 80 characters. Firm pattern: a short description, a hyphen, then the account number - <strong>&ldquo;{example}&rdquo;</strong>. Use the account number, never the client or household name.</>];
}

// The first step of every guide: launch New Case, pick the record type, then
// fill in the "New Case: <record type>" header screen. The flow presets
// Department from the record type and hides it, so users never pick it.
// lookups: [[label, description]] - the pickers that appear once Category and
// Sub-Category are both chosen (Household, Advisor, Office - varies by flow).
export function startCaseStep({ recordType, category, subCategory, subject, lookups = [], note }) {
  return {
    title: 'Start the case and fill in the header',
    body: (
      <>
        <p style={{ marginBottom: '0.75rem' }}>
          Click <strong>New Case</strong>. On the <strong>Create New Case</strong> screen, under{' '}
          <em>Select a Record Type</em>, choose <strong>{recordType}</strong> and click Next.
          The <strong>New Case: {recordType}</strong> screen asks for:
        </p>
        <FieldGrid rows={[
          subject,
          ['Due Date', 'Required. Defaults to today - change it if you need this by a later date.'],
          ['Category', category],
          ['Sub-Category', subCategory],
          ...lookups,
        ]} />
        <p style={{ margin: 0 }}>
          Sub-Category only offers values valid for the Category above it, and the
          {lookups.length ? ` ${lookups.map(l => l[0]).join(' / ')} picker${lookups.length > 1 ? 's appear' : ' appears'}` : ' rest of the form appears'} only
          once both are chosen.{note ? <> {note}</> : null}
        </p>
      </>
    ),
  };
}

// Every question on a screen, in the order it can appear, with the condition
// that reveals it.
// questions: [{ q, when, options?, note?, warn?, screen? }]
// A `screen` value starts a new sub-heading (the FSC screen title) above that question.
export function QuestionList({ questions }) {
  let n = 0;
  return (
    <div>
      {questions.map((item, i) => {
        n += 1;
        return (
          <React.Fragment key={`${item.q}-${i}`}>
            {item.screen && (
              <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: brand.deepBlue, margin: i === 0 ? '0 0 0.25rem' : '1.25rem 0 0.25rem' }}>
                {item.screen}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.9rem', padding: '0.9rem 0', borderTop: `1px solid ${brand.softGray}` }}>
              <div style={{ flex: '0 0 auto', fontWeight: 800, fontSize: '0.72rem', color: brand.goldenrod, paddingTop: '0.15rem', minWidth: '24px' }}>
                Q{n}
              </div>
              <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: brand.deepBlue, fontSize: '0.9rem', marginBottom: '0.35rem' }}>
                  {item.q}
                </div>
                <div style={{ fontSize: '0.78rem', color: '#7c7c80', marginBottom: item.options || item.note ? '0.45rem' : 0 }}>
                  <span style={{ fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: brand.tealBlue, marginRight: '0.5rem' }}>Shown</span>
                  {item.when}
                </div>
                {item.options && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: item.note ? '0.45rem' : 0 }}>
                    {item.options.map(opt => (
                      <span key={opt} style={{ fontSize: '0.78rem', padding: '0.1rem 0.6rem', border: `1px solid ${brand.softGray}`, borderRadius: '12px', color: brand.deepBlue, background: '#fbfaf8' }}>
                        {opt}
                      </span>
                    ))}
                  </div>
                )}
                {item.note && (
                  <div style={{ fontSize: '0.83rem', color: item.warn ? '#8a5a00' : brand.charcoal, maxWidth: '64ch' }}>
                    {item.warn && <i className="bi bi-exclamation-triangle-fill me-2" style={{ color: brand.goldenrod }} />}
                    {item.note}
                  </div>
                )}
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// The acknowledgement notice at the foot of every submit screen. The wording
// is the same across the Operations and Trading flows; `team` sets which
// cutoff leads and which inbox is named first.
const SERVICE_ROWS = [
  ['In good order, before 2:30 p.m. ET',
   'Allow 1-2 business days where trading is required. Requests are worked in the order received.'],
  ['Outstanding Prosperity paperwork',
   'Operations and Trading cannot raise cash from (or distribute from) the account. The ticket receives a NIGO while the team works with you to bring the account back in good order.'],
  ['Separately Managed Accounts',
   'Prosperity relays instructions to the third-party manager rather than trading directly. Individual buy/sell orders on an SMA are rejected - submit a separate ticket to Trading to liquidate or reallocate.'],
  ['Incomplete or unclear instructions',
   'The request may be held or returned. Execution timing and pricing depend on market conditions, custodian requirements and trade cutoffs, and cannot be guaranteed.'],
];

// confirmations={false} drops the confirmations-and-statements line, for
// requests with no client account (staff changes, Salesforce admin).
export function WhatHappensNext({ team = 'operations', before, rows = SERVICE_ROWS, after, confirmations = true }) {
  const th = { textAlign: 'left', borderBottom: `2px solid ${brand.softGray}`, color: brand.deepBlue, fontSize: '0.72rem', letterSpacing: '0.07em', textTransform: 'uppercase' };
  const td = { borderBottom: `1px solid ${brand.softGray}`, verticalAlign: 'top' };
  return (
    <Section title="What happens next" icon="clock-history">
      {before}
      <Callout tone="warn">
        {team === 'trading'
          ? <>The trade cutoff is <strong>2:30 p.m. ET</strong>. After that, or outside market hours, requests are worked on a best-efforts basis and may not be placed until the next business day; mutual fund orders receive the next available NAV. The submit screen shows a live countdown to the cutoff.</>
          : <>Requests submitted after <strong>2:30 p.m. ET</strong>, or on a non-business day, are worked on a best-efforts basis and may not be initiated until the next business day. The submit screen shows a live countdown to the cutoff.</>}
      </Callout>

      {rows && rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', color: brand.charcoal }}>
            <thead>
              <tr>
                <th style={{ ...th, padding: '0.5rem 1rem 0.5rem 0', whiteSpace: 'nowrap' }}>Situation</th>
                <th style={{ ...th, padding: '0.5rem 0' }}>What to expect</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([situation, expect]) => (
                <tr key={situation}>
                  <td style={{ ...td, padding: '0.6rem 1.5rem 0.6rem 0', fontWeight: 600, color: brand.deepBlue }}>{situation}</td>
                  <td style={{ ...td, padding: '0.6rem 0' }}>{expect}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {after}

      {confirmations && (
        <p style={{ color: brand.charcoal, fontSize: '0.875rem', marginTop: '1rem', marginBottom: '0.5rem' }}>
          Review the resulting confirmations and statements promptly and report any error or discrepancy
          within <strong>2 business days</strong> of receipt.
        </p>
      )}
      <p style={{ color: brand.charcoal, fontSize: '0.875rem', margin: 0, marginTop: confirmations ? 0 : '1rem' }}>
        Questions on a submitted case:{' '}
        <a href={`mailto:${TEAM_EMAIL[team]}`} style={{ color: brand.tealBlue }}>{TEAM_EMAIL[team]}</a>.
        {' '}{team === 'trading' ? 'Operations questions:' : 'Trading-specific questions:'}{' '}
        <a href={`mailto:${TEAM_EMAIL[team === 'trading' ? 'operations' : 'trading']}`} style={{ color: brand.tealBlue }}>
          {TEAM_EMAIL[team === 'trading' ? 'operations' : 'trading']}
        </a>.
      </p>
    </Section>
  );
}

// "If something looks wrong". items: [{ h, p: [string | node, ...] }]
export function Trouble({ items }) {
  return (
    <Section title="If something looks wrong" icon="tools">
      <div style={{ display: 'grid', gap: '1rem' }}>
        {items.map(item => (
          <div key={item.h}>
            <div style={{ fontWeight: 700, color: brand.deepBlue, fontSize: '0.9rem', marginBottom: '0.3rem' }}>{item.h}</div>
            {item.p.map((t, i) => (
              <p key={i} style={{ color: brand.charcoal, fontSize: '0.875rem', marginBottom: '0.4rem', maxWidth: '68ch' }}>{t}</p>
            ))}
          </div>
        ))}
      </div>
    </Section>
  );
}

export function Footnote({ children }) {
  return (
    <p style={{ color: '#7c7c80', fontSize: '0.78rem', maxWidth: '68ch', marginTop: '0.5rem' }}>{children}</p>
  );
}

// Shared trouble entries - the same dependent-picklist and account-table
// behaviour applies to every Operations and Trading flow.
export const TROUBLE_CATEGORY_MISSING = (recordType, category) => ({
  h: `${category} isn't in the Category list`,
  p: [
    `Category is filtered by the record type you chose on the first screen, and Sub-Category by Category. If ${category} is missing you chose a record type other than ${recordType} - cancel and start again.`,
  ],
});

// `filters` is the guide's own sentence on which rows that flow's tables
// leave out - the filters differ per flow, so it is never assumed here.
export const troubleSleeveRows = filters => ({
  h: "The account I expected isn't in the list",
  p: [
    'The account tables list sleeves, not just the account as a whole - you will see rows like 0989-022356_DFA TM 100/0. The Account Details column shows the sleeve name, the registration type with -Q (qualified) or -NQ, and the registration name. Pick the sleeve the money actually sits in.',
    filters,
  ],
});

export const TROUBLE_WRONG_HOUSEHOLD = {
  h: 'I picked the wrong household',
  p: [
    'The household chosen on the first screen decides which accounts you can pick later, and the case is stamped with it. If the account lists look wrong, cancel and start again rather than working around it.',
  ],
};

Section.propTypes = { title: PropTypes.string.isRequired, icon: PropTypes.string.isRequired, children: PropTypes.node };
GuidePage.propTypes = {
  title: PropTypes.string.isRequired, icon: PropTypes.string.isRequired, path: PropTypes.node.isRequired,
  minutes: PropTypes.number, intro: PropTypes.node, children: PropTypes.node,
};
Para.propTypes = { children: PropTypes.node, last: PropTypes.bool };
Bullets.propTypes = { items: PropTypes.arrayOf(PropTypes.node).isRequired };
Callout.propTypes = { children: PropTypes.node, tone: PropTypes.oneOf(['info', 'warn']) };
FieldGrid.propTypes = { rows: PropTypes.arrayOf(PropTypes.array).isRequired };
Steps.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({ title: PropTypes.string.isRequired, body: PropTypes.node })).isRequired,
};
QuestionList.propTypes = {
  questions: PropTypes.arrayOf(PropTypes.shape({
    q: PropTypes.string.isRequired, when: PropTypes.string.isRequired, options: PropTypes.arrayOf(PropTypes.string),
    note: PropTypes.node, warn: PropTypes.bool, screen: PropTypes.string,
  })).isRequired,
};
WhatHappensNext.propTypes = {
  team: PropTypes.oneOf(['operations', 'trading']), before: PropTypes.node, rows: PropTypes.array, after: PropTypes.node,
  confirmations: PropTypes.bool,
};
Trouble.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({ h: PropTypes.string.isRequired, p: PropTypes.arrayOf(PropTypes.node).isRequired })).isRequired,
};
Footnote.propTypes = { children: PropTypes.node };
